import {
    CLIENT_TRANSLATIONS_CACHE_MAX_AGE,
    DEBUG_LANGUAGE,
    DEFAULT_LANGUAGE,
    I18N_CLIENT_NS,
    I18N_COUNTRY_CODE_MAPPING,
    SUPPORTED_LANGUAGES,
} from "../common/constants";
import Utils from "./utils";

export default class I18n {
    i18next = window.i18next || {};

    constructor(lang) {
        if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
            lang = DEFAULT_LANGUAGE;
        }
        this._initialized = false;
        this._language = lang;
        this.SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGES;
    }

    /**
     * Initialize i18n provider or change target language for translations if already initialized
     * @param {string} [lang]           target language for translations
     * @returns {promise<boolean>}      true if initialized and configured with the target language
     */
    async init(lang) {
        if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
            lang = this._language || DEFAULT_LANGUAGE;
        }
        if (this._initialized) {
            return this._changeLanguage(lang);
        }
        const chainedBackend = window.i18nextChainedBackend || {};
        try {
            await this.i18next.use(chainedBackend).init(this._i18nextConfig(lang));
            this._initialized = true;
            this._language = lang;
            window.t = this.translate.bind(this);
            // Use VueI18n instance for component interpolation only
            this.vueI18n = new window.VueI18n({
                formatFallbackMessages: true,
                silentTranslationWarn: true,
            });
            console.log("i18n init success");
        } catch (err) {
            console.error(`i18n init error: ${err.message}`);
        }
        return this._initialized && this._language === lang;
    }

    /**
     * Translate a source string key into the configured target language
     * @param {string|string[]} source  Translation key or array for multiple fallback keys
     * @param {string} [defaultValue]   Default translation. Can be omitted instead of passing null
     * @param {object} [options]        Extra parameters (depends on i18n provider: https://www.i18next.com/translation-function/essentials)
     * @returns {string}                target || default || source
     *
     * @example Translate
     *  translate('ERROR.Kobold')
     * @example Translate, with a default value fallback
     *  translate('ERROR.Kobold', 'The kobold ate my homework.')
     * @example Translate, with a fallback to a different key, and a default value fallback
     *  translate(['ERROR.Goblin', 'ERROR.Kobold', 'ERROR.Generic'], 'Something ate my homework.')
     * @example Translate, with interpolation, and a default value fallback
     *  translate('ERROR.Kobold', 'The kobold ate my {{what}}.', {what: 'homework'})
     */
    translate(...args) {
        if (!this._initialized) {
            return args[0];
        }
        return this.i18next.t(...args);
    }

    /**
     * As translate, but sets interpolation.escapeValue to false.
     * This should only be used in cases where the framework already escapes unsafe HTML.
     * @param {string|string[]} source  Translation key or array for multiple fallback keys
     * @param {string} [defaultValue]   Default translation. Can be omitted instead of passing null
     * @param {object} [options]        Extra parameters (depends on i18n provider: https://www.i18next.com/translation-function/essentials)
     * @returns {string}                target || default || source
     */
    translateNoEscape(...args) {
        if (!this._initialized) {
            return args[0];
        }
        const options = {};
        if (args[args.length - 1] && Utils.isObject(args[args.length - 1])) {
            Object.assign(options, args[args.length - 1]);
        }
        if (args[1] && typeof args[1] === "string") {
            options.defaultValue = args[1];
        }
        options.interpolation = options.interpolation || {};
        options.interpolation.escapeValue = false;
        return this.i18next.t(args[0], options);
    }

    /**
     * Translate a source string key into a target language in Handlebars.
     * Handlebars helpers pass context as options as a last argument and this includes hash arguments.
     * Optional arguments, such as defaultValue, should be passed into Handlebars context as hash arguments.
     * @param {string} source   Translation key
     * @param {object} options  Handlebars context which contains data such as language and hash arguments
     * @returns {string}        target || default || source
     *
     * @example Translate, with a default value fallback
     *  {{t 'ERROR.Goblin' defaultValue='The goblin ate my homework.'}}
     * @example Translate, with interpolation, and a default value fallback
     *  {{t 'ERROR.Goblin' defaultValue='The goblin ate my {{what}}.'} what='homework'}
     */
    hbsTranslate(source, options) {
        if (!Utils.isObject(options)) {
            console.warn(`hbs options is not an object: client hbsTranslate(${source})`);
            return source;
        }
        if (options.hash) {
            options.hash.defaultValue = options.hash.defaultValue || options.hash.default;
        }
        return this.translate(source, options.hash);
    }

    /**
     * Extract the country code from a language or language-region pair
     * @param lang       Input language or language-region pair
     * @returns {string} Lowercase ISO 3166-1-alpha-2 country code, e.g. us
     */
    langToCountryCode(lang) {
        // Ensure supported language
        if (!SUPPORTED_LANGUAGES.includes(lang) || lang === DEBUG_LANGUAGE) {
            lang = DEFAULT_LANGUAGE;
        }
        let countryCode;
        const match = lang.match(/([a-zA-Z]{2})(?:-([A-Z]{2}))?/i);
        if (match) {
            // If there is a match, match[1] will never be null
            lang = match[1].toLowerCase();
            // There is not always a match to the country code in the second capture group
            if (match[2]) {
                countryCode = match[2].toLowerCase();
            }
        }
        return countryCode || I18N_COUNTRY_CODE_MAPPING[lang];
    }

    /**
     * Change target language for translations
     * @param {string} lang         target language for translations
     * @returns {promise<boolean>}  returns true if configured with target language
     * @private
     */
    async _changeLanguage(lang) {
        if (this._language === lang) {
            return true;
        }
        try {
            await this.i18next.changeLanguage(lang);
            this._language = lang;
            console.log("i18n changeLanguage success");
        } catch (err) {
            console.error(`i18n changeLanguage error: ${err.message}`);
        }
        return this._language === lang;
    }

    /**
     * Retrieve config object for i18n provider init
     * @param {string} lang target language for translations
     * @returns {object}
     * @private
     */
    _i18nextConfig(lang) {
        const httpBackend = window.i18nextHttpBackend || {};
        const localStorageBackend = window.i18nextLocalStorageBackend || {};
        return {
            returnNull: true, // Set to maintain behaviour. Default value was changed to false in v23 of i18next.
            debug: lang === DEBUG_LANGUAGE,
            saveMissing: lang === DEBUG_LANGUAGE,
            lng: lang,
            fallbackLng: DEFAULT_LANGUAGE,
            nsSeparator: null,
            load: "currentOnly",
            // files to load
            ns: I18N_CLIENT_NS,
            // default namespace (needs no ns option on calling t)
            defaultNS: "client",
            // fallback, can be a string or an array of namespaces
            fallbackNS: "common",
            backend: {
                backends: [
                    localStorageBackend, // primary backend
                    httpBackend, // secondary backend, fetch from server
                ],
                backendOptions: [
                    {
                        allowMultiLoading: false,
                        expirationTime: lang === DEBUG_LANGUAGE ? 1 : CLIENT_TRANSLATIONS_CACHE_MAX_AGE,
                    },
                    {
                        loadPath: this._loadPath,
                        addPath: "/api/i18n/add/{{lng}}/{{ns}}",
                    },
                ],
            },
        };
    }

    /**
     * Util to get path to translation dictionaries when path varies with config
     * Inputs are single array lengths when allowMultiLoading: false.
     * @param {array<string>} lng   Language as specified by 'lng' in i18next config options
     * @param {array<string>} ns    Namespace as specified by 'ns' in i18next config options
     * @returns {string}            Path to translation dictionary
     */
    _loadPath(lng, ns) {
        if (lng[0] === DEBUG_LANGUAGE) {
            return `/api/i18n/${lng[0]}/${ns[0]}.json`;
        }
        return `/locales/${lng[0]}/${ns[0]}.json`;
    }
}
